export interface ErrorInfo {
  title: string;
  message: string;
  type: 'error' | 'warning' | 'info';
  retryable: boolean;
  duration?: number;
}

export class AppError extends Error {
  public readonly code: string;
  public readonly retryable: boolean;
  public readonly userMessage: string;

  constructor(
    message: string,
    code: string = 'UNKNOWN_ERROR',
    retryable: boolean = false,
    userMessage?: string
  ) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.retryable = retryable;
    this.userMessage = userMessage || message;
  }
}

export const createNetworkError = (message?: string): AppError => {
  return new AppError(
    'Network request failed',
    'NETWORK_ERROR',
    true,
    message || 'Zkontroluj připojení k internetu a zkus to znovu.'
  );
};

export const createAuthError = (message?: string): AppError => {
  return new AppError(
    'Authentication failed',
    'AUTH_ERROR',
    true,
    message || 'Přihlášení se nezdařilo. Zkus to prosím znovu.'
  );
};

export const createCheckoutError = (message?: string): AppError => {
  return new AppError(
    'Checkout failed',
    'CHECKOUT_ERROR',
    true,
    message || 'Nepodařilo se otevřít checkout. Zkus to prosím znovu.'
  );
};

export const createTokenError = (message?: string): AppError => {
  return new AppError(
    'Token refresh failed',
    'TOKEN_ERROR',
    true,
    message || 'Problém s ověřením. Zkus se přihlásit znovu.'
  );
};

export const createDateError = (message?: string): AppError => {
  return new AppError(
    'Date calculation failed',
    'DATE_ERROR',
    false,
    message || 'Problém s výpočtem data.'
  );
};

export const createStorageError = (message?: string): AppError => {
  return new AppError(
    'Storage operation failed',
    'STORAGE_ERROR',
    true,
    message || 'Problém s uložením dat.'
  );
};

export const createStreakError = (message?: string): AppError => {
  return new AppError(
    'Streak operation failed',
    'STREAK_ERROR',
    true,
    message || 'Problém se streak systémem.'
  );
};

export const parseErrorMessage = (error: unknown): ErrorInfo => {
  // Handle AppError instances
  if (error instanceof AppError) {
    return {
      title: getErrorTitle(error.code),
      message: error.userMessage,
      type: 'error',
      retryable: error.retryable,
      duration: 7000
    };
  }

  // Handle network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      title: 'Problém s připojením',
      message: 'Zkontroluj připojení k internetu a zkus to znovu.',
      type: 'error',
      retryable: true,
      duration: 7000
    };
  }

  // Handle Firebase errors
  if (error && typeof error === 'object' && 'code' in error) {
    const firebaseError = error as { code: string; message: string };
    return parseFirebaseError(firebaseError);
  }

  // Handle HTTP errors
  if (error && typeof error === 'object' && 'status' in error) {
    const httpError = error as { status: number; statusText?: string };
    return parseHttpError(httpError);
  }

  // Handle generic errors
  return {
    title: 'Něco se pokazilo',
    message: 'Zkus to prosím znovu za chvilku.',
    type: 'error',
    retryable: true,
    duration: 7000
  };
};

const getErrorTitle = (code: string): string => {
  switch (code) {
    case 'NETWORK_ERROR':
      return 'Problém s připojením';
    case 'AUTH_ERROR':
      return 'Problém s přihlášením';
    case 'CHECKOUT_ERROR':
      return 'Problém s platbou';
    case 'TOKEN_ERROR':
      return 'Problém s ověřením';
    case 'DATE_ERROR':
      return 'Problém s datem';
    case 'STORAGE_ERROR':
      return 'Problém s uložením';
    case 'STREAK_ERROR':
      return 'Problém se streak';
    default:
      return 'Něco se pokazilo';
  }
};

const parseFirebaseError = (error: { code: string; message: string }): ErrorInfo => {
  switch (error.code) {
    case 'auth/network-request-failed':
      return {
        title: 'Problém s připojením',
        message: 'Zkontroluj připojení k internetu a zkus to znovu.',
        type: 'error',
        retryable: true
      };
    case 'auth/too-many-requests':
      return {
        title: 'Příliš mnoho pokusů',
        message: 'Zkus to prosím za chvilku.',
        type: 'warning',
        retryable: true,
        duration: 10000
      };
    case 'auth/user-disabled':
      return {
        title: 'Účet je zablokován',
        message: 'Kontaktuj podporu: podpora@opravech.cz',
        type: 'error',
        retryable: false
      };
    case 'permission-denied':
      return {
        title: 'Nedostatečná oprávnění',
        message: 'Zkus se přihlásit znovu.',
        type: 'error',
        retryable: true
      };
    default:
      return {
        title: 'Problém se službou',
        message: 'Zkus to prosím znovu za chvilku.',
        type: 'error',
        retryable: true
      };
  }
};

const parseHttpError = (error: { status: number; statusText?: string }): ErrorInfo => {
  switch (error.status) {
    case 400:
      return {
        title: 'Neplatný požadavek',
        message: 'Zkontroluj zadané údaje a zkus to znovu.',
        type: 'error',
        retryable: false
      };
    case 401:
      return {
        title: 'Problém s ověřením',
        message: 'Zkus se přihlásit znovu.',
        type: 'error',
        retryable: true
      };
    case 403:
      return {
        title: 'Nedostatečná oprávnění',
        message: 'Nemáš oprávnění k této akci.',
        type: 'error',
        retryable: false
      };
    case 404:
      return {
        title: 'Služba není dostupná',
        message: 'Zkus to prosím později.',
        type: 'error',
        retryable: true
      };
    case 429:
      return {
        title: 'Příliš mnoho požadavků',
        message: 'Zkus to prosím za chvilku.',
        type: 'warning',
        retryable: true,
        duration: 10000
      };
    case 500:
    case 502:
    case 503:
    case 504:
      return {
        title: 'Problém se serverem',
        message: 'Zkus to prosím za chvilku.',
        type: 'error',
        retryable: true
      };
    default:
      return {
        title: 'Něco se pokazilo',
        message: 'Zkus to prosím znovu.',
        type: 'error',
        retryable: true
      };
  }
};

export const createRetryableAction = <T extends any[], R>(
  action: (...args: T) => Promise<R>,
  maxRetries: number = 3,
  delay: number = 1000
) => {
  return async (...args: T): Promise<R> => {
    let lastError: unknown;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await action(...args);
      } catch (error) {
        lastError = error;
        
        // Don't retry on the last attempt or if error is not retryable
        if (attempt === maxRetries) {
          throw error;
        }
        
        const errorInfo = parseErrorMessage(error);
        if (!errorInfo.retryable) {
          throw error;
        }
        
        // Wait before retrying with exponential backoff
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
      }
    }
    
    throw lastError;
  };
};

export const withErrorHandling = <T extends any[], R>(
  action: (...args: T) => Promise<R>,
  onError?: (error: ErrorInfo) => void
) => {
  return async (...args: T): Promise<R | null> => {
    try {
      return await action(...args);
    } catch (error) {
      const errorInfo = parseErrorMessage(error);
      
      if (onError) {
        onError(errorInfo);
      } else {
        console.error('Unhandled error:', error);
      }
      
      return null;
    }
  };
};

export const withAnimationErrorHandling = <T extends any[], R>(
  action: (...args: T) => R,
  fallbackValue?: R
) => {
  return (...args: T): R | undefined => {
    try {
      return action(...args);
    } catch (error) {
      console.warn('Animation error handled gracefully:', error);
      return fallbackValue;
    }
  };
};

export const withDateErrorHandling = <T extends any[], R>(
  action: (...args: T) => R,
  fallbackValue?: R,
  onError?: (error: unknown) => R
) => {
  return (...args: T): R | undefined => {
    try {
      return action(...args);
    } catch (error) {
      if (onError) {
        return onError(error);
      } else {
        console.warn('Date calculation error handled:', error);
        return fallbackValue;
      }
    }
  };
};

export const withStorageErrorHandling = <T extends any[], R>(
  action: (...args: T) => R,
  fallbackValue: R,
  onError?: (error: unknown) => R
) => {
  return (...args: T): R => {
    try {
      return action(...args);
    } catch (error) {
      if (onError) {
        return onError(error);
      } else {
        console.warn('Storage operation error handled:', error);
        return fallbackValue;
      }
    }
  };
};

export const withStreakErrorHandling = <T extends any[], R>(
  action: (...args: T) => R,
  fallbackValue?: R,
  onError?: (error: unknown) => void
) => {
  return (...args: T): R | undefined => {
    try {
      return action(...args);
    } catch (error) {
      if (onError) {
        onError(error);
      } else {
        console.warn('Streak operation error handled:', error);
      }
      return fallbackValue;
    }
  };
};